import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { MessageCircle, Trash2, Plus, Minus, ShoppingBag, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const CartContent = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, clearCart, totalItems, whatsappCheckout } = useCart();
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setScrollTop(window.scrollY || window.pageYOffset || 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay - absolute positioned at scroll position */}
      <div
        style={{
          position: 'absolute',
          top: scrollTop,
          left: 0,
          width: '100%',
          height: '100vh',
          zIndex: 9998,
          backgroundColor: 'rgba(0,0,0,0.6)',
        }}
        onClick={() => setIsOpen(false)}
        data-testid="cart-overlay"
      />

      {/* Drawer - absolute positioned at scroll position */}
      <div
        style={{
          position: 'absolute',
          top: scrollTop,
          right: 0,
          width: '320px',
          maxWidth: '85vw',
          height: '100vh',
          zIndex: 9999,
        }}
        className="bg-background shadow-2xl flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground" data-testid="cart-title">
            <ShoppingBag className="w-5 h-5" />
            Cart ({totalItems})
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
            data-testid="cart-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-5" data-testid="cart-empty">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">Your cart is empty</p>
            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} asChild>
              <Link to="/products">Browse Products</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="cart-items">
              {items.map((item) => (
                <div
                  key={item.key}
                  className="flex gap-3 p-3 rounded-xl bg-muted/30 border border-border"
                  data-testid={`cart-item-${item.key}`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gold font-medium uppercase tracking-wider">{item.brand}</p>
                    <p className="text-xs font-semibold text-foreground line-clamp-2 leading-tight">{item.name}</p>
                    {item.size && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">{item.size}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => updateQty(item.key, item.qty - 1)}
                        disabled={item.qty <= 1}
                        className="w-6 h-6 rounded border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40 text-xs"
                        data-testid={`qty-minus-${item.key}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-bold w-5 text-center" data-testid={`qty-value-${item.key}`}>
                        {item.qty}
                      </span>
                      <button
                        onClick={() => updateQty(item.key, item.qty + 1)}
                        className="w-6 h-6 rounded border border-border flex items-center justify-center hover:bg-muted transition-colors text-xs"
                        data-testid={`qty-plus-${item.key}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeItem(item.key)}
                        className="ml-auto w-6 h-6 rounded flex items-center justify-center text-destructive hover:bg-destructive/10 transition-colors"
                        data-testid={`remove-item-${item.key}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t border-border px-5 py-4 space-y-2.5 bg-muted/20 flex-shrink-0">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Items</span>
                <span className="font-bold text-foreground">{totalItems}</span>
              </div>
              <Button
                size="lg"
                className="w-full text-sm py-5"
                onClick={whatsappCheckout}
                data-testid="cart-whatsapp-checkout"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Order on WhatsApp
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={clearCart}
                  data-testid="cart-clear"
                >
                  Clear Cart
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setIsOpen(false)}
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const CartDrawer = () => {
  return createPortal(<CartContent />, document.body);
};
